// Host-side feature-flag consumption (ENG-6251, ADR-0022 slice 3).
//
// The customer-host trust boundary (ADR-0022) keeps the vendor OpenFeature SDK
// and any targeting rules OFF the host — the API evaluates flags centrally and
// the heartbeat delivers a pre-resolved value map. This module turns that map
// into a typed accessor with operator-override precedence:
//
//   env var (operator override)  ->  heartbeat value  ->  compiled-in default
//
// Values are re-read every poll, so a DB flip reaches a running manager within
// one heartbeat cycle (~5m) with no `systemctl restart`. The last-known-good
// map is cached to `~/.augmented/flags-cache.json`; on API unreachability the
// cache is used, then the per-flag declared safe default (the registry default)
// as the floor. Unknown keys in the map are stored verbatim and ignored at
// resolution time, so registry skew between a newer API and an older CLI never
// drops or throws.
//
// tsup bundles `@augmented/core` inline, so the imports below resolve to the
// compiled registry shipped in the CLI binary — core changes need both a core
// and a CLI rebuild to reach hosts.

import { existsSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';
import {
  coerceEnvValue,
  getFlagDefinition,
  listFlagDefinitions,
  normalizeFlagValue,
  type FlagDefinition,
  type FlagValue,
} from '@augmented/core';
import { atomicWriteFileSync } from './atomic-write.js';

/** Which layer a resolved value came from (for `agt flags resolve` + logs). */
export type FlagSource = 'env' | 'heartbeat-cache' | 'default';

export interface ResolvedFlag {
  key: string;
  value: FlagValue;
  source: FlagSource;
  /** The legacy env var that can override this flag, if any. */
  envVar?: string;
  /**
   * True when an env override is in force AND a differing heartbeat value is
   * present — i.e. the operator override is masking a central flip. Surfaced
   * so `agt flags resolve` and the boot logs make a "DB change that isn't
   * taking effect" diagnosable (ADR-0022 §5).
   */
  envMasksHeartbeat?: boolean;
}

/** On-disk shape of `~/.augmented/flags-cache.json`. */
export interface FlagsCacheFile {
  /** `flags_schema_version` reported by the API on the cached heartbeat. */
  schema_version: string;
  /** ISO timestamp the cache was last written. */
  updated_at: string;
  /**
   * The heartbeat map verbatim — full map, including keys this CLI doesn't
   * recognise. Never pruned, so an older CLI round-trips a newer API's flags.
   */
  flags: Record<string, FlagValue>;
  /**
   * ENG-7682 follow-up: the last-known-good per-agent notify-dispatch map,
   * keyed by agent_id, persisted alongside the host-wide `flags` map so a
   * manager restart hydrates BOTH from the same snapshot in init(). Without
   * this, init() would restore the host-wide value but reset the per-agent map
   * to {}, and an agent session spawned before the next new-schema heartbeat
   * would receive the host-wide value instead of its last-known per-agent one.
   * Optional so a cache written by an older CLI (no field) reads back as {}.
   */
  notify_dispatch_by_agent?: Record<string, FlagValue>;
}

export function defaultFlagsCachePath(configDir: string): string {
  return join(configDir, 'flags-cache.json');
}

/**
 * Read the flags cache tolerantly: a missing file, malformed JSON, or a
 * non-object payload all collapse to `null` (caller falls back to compiled
 * defaults). Never throws.
 */
export function readFlagsCache(path: string): FlagsCacheFile | null {
  try {
    if (!existsSync(path)) return null;
    const parsed = JSON.parse(readFileSync(path, 'utf8')) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    const flags = obj['flags'];
    if (!flags || typeof flags !== 'object') return null;
    // ENG-7682 follow-up: restore the per-agent notify-dispatch map tolerantly.
    // A missing field (older CLI wrote the cache) or a non-object payload reads
    // back as {} so init() hydrates an empty map rather than throwing.
    const perAgent = obj['notify_dispatch_by_agent'];
    return {
      schema_version: typeof obj['schema_version'] === 'string' ? obj['schema_version'] : '',
      updated_at: typeof obj['updated_at'] === 'string' ? obj['updated_at'] : '',
      flags: { ...(flags as Record<string, FlagValue>) },
      notify_dispatch_by_agent:
        perAgent && typeof perAgent === 'object'
          ? { ...(perAgent as Record<string, FlagValue>) }
          : {},
    };
  } catch {
    return null;
  }
}

export function writeFlagsCache(path: string, file: FlagsCacheFile): void {
  atomicWriteFileSync(path, `${JSON.stringify(file, null, 2)}\n`);
}

/**
 * Age of a cache file in seconds, preferring its recorded `updated_at` and
 * falling back to the file mtime. `null` when neither is available — caller
 * renders "unknown" rather than a misleading 0.
 */
export function flagsCacheAgeSeconds(
  cache: FlagsCacheFile,
  path: string,
  now: Date = new Date(),
): number | null {
  const fromRecorded = Date.parse(cache.updated_at);
  if (!Number.isNaN(fromRecorded)) {
    return Math.max(0, (now.getTime() - fromRecorded) / 1000);
  }
  try {
    return Math.max(0, (now.getTime() - statSync(path).mtimeMs) / 1000);
  } catch {
    return null;
  }
}

/**
 * Resolve one flag across the layers. Pure — every input is explicit so the
 * `agt flags resolve` CLI (a separate process from the manager) and the manager
 * itself share identical precedence.
 */
export function resolveFlagFromLayers(
  definition: FlagDefinition,
  heartbeatFlags: Record<string, FlagValue> | undefined,
  env: NodeJS.ProcessEnv,
): ResolvedFlag {
  const envValue = definition.envVar
    ? coerceEnvValue(definition, env[definition.envVar])
    : undefined;
  const hbValue = heartbeatFlags
    ? normalizeFlagValue(definition, heartbeatFlags[definition.key])
    : undefined;

  if (envValue !== undefined) {
    return {
      key: definition.key,
      value: envValue,
      source: 'env',
      envVar: definition.envVar,
      envMasksHeartbeat: hbValue !== undefined && hbValue !== envValue,
    };
  }
  if (hbValue !== undefined) {
    return { key: definition.key, value: hbValue, source: 'heartbeat-cache' };
  }
  return { key: definition.key, value: definition.defaultValue, source: 'default' };
}

/** Resolve every registered flag against the given heartbeat map + env. */
export function resolveAllFlags(
  heartbeatFlags: Record<string, FlagValue> | undefined,
  env: NodeJS.ProcessEnv = process.env,
): ResolvedFlag[] {
  return listFlagDefinitions().map((definition) =>
    resolveFlagFromLayers(definition, heartbeatFlags, env),
  );
}

type LogFn = (msg: string) => void;

/**
 * The manager's live flag store. Holds the last heartbeat map in memory, mirrors
 * it to disk for offline/boot use, logs every value transition, and warns when
 * an env override masks a differing heartbeat value.
 */
export class HostFlagStore {
  private heartbeatFlags: Record<string, FlagValue> = {};
  /**
   * ENG-7682 follow-up: per-agent notify-dispatch values from the heartbeat,
   * keyed by agent_id. Only agents whose resolved value DIFFERS from the
   * host-wide value appear here; everyone else falls back to the host-wide
   * `notify-dispatch` entry. This is what stops one agent's opt-in from fanning
   * out to every agent the host runs. Held in memory only (re-sent every
   * heartbeat), not persisted to the flags cache, which is a host-wide map.
   */
  private notifyDispatchByAgent: Record<string, FlagValue> = {};
  /**
   * ENG-7891 / ADR-0049 slice 3: per-agent boolean overrides from the heartbeat,
   * keyed `agent_id -> flag_key -> bool`. This is the generic per-agent seam
   * `getBooleanForAgent` reads so a boolean gate (today only
   * `id-keyed-layout-migration`) can be armed one agent at a time. Held in
   * memory only and re-sent every heartbeat - deliberately NOT persisted to the
   * flags cache (which is a host-wide map), so a restart cleanly falls back to
   * the host-wide value until the next heartbeat rather than pinning a stale
   * per-agent opt-in. Empty until the API sends `feature_flags_by_agent` (the
   * flag ships dark, so it stays empty and every agent resolves host-wide).
   */
  private booleanFlagsByAgent: Record<string, Record<string, boolean>> = {};
  /**
   * The `flags_schema_version` from the most recent heartbeat, retained so a
   * per-agent-map write (which carries no schema version of its own) persists
   * the same schema string the host-wide map was last written with rather than
   * clobbering it to empty. Hydrated from the cache on init().
   */
  private lastSchemaVersion = '';
  private readonly cachePath: string;
  private readonly log: LogFn;
  private readonly env: NodeJS.ProcessEnv;
  private initialised = false;
  /**
   * Per-flag record of the heartbeat value we last warned about being masked by
   * an env override. Used to log the env-masking WARN only on *transition* (when
   * masking newly begins or the masked-over heartbeat value changes) rather than
   * on every heartbeat — a stable, intentional operator override (e.g. a canary
   * pin in `agt-manager.env`) otherwise re-logs the same WARN every ~90s forever
   * (ENG-6478). A key is deleted when masking stops, so a later re-mask warns again.
   */
  private readonly lastMaskWarn = new Map<string, FlagValue>();

  constructor(opts: { cachePath: string; log?: LogFn; env?: NodeJS.ProcessEnv }) {
    this.cachePath = opts.cachePath;
    this.log = opts.log ?? (() => {});
    this.env = opts.env ?? process.env;
  }

  /**
   * Boot-time hydrate from the last-known-good cache. Cached values are used
   * only until the first successful heartbeat; the cache age is logged so a
   * stale `flags-cache.json` is visible (ADR-0022 §5 restart-with-stale-cache).
   */
  init(now: Date = new Date()): void {
    if (this.initialised) return;
    this.initialised = true;
    const cached = readFlagsCache(this.cachePath);
    if (!cached) {
      this.log('[flags] no last-known-good cache on disk; using compiled defaults until first heartbeat');
      return;
    }
    this.heartbeatFlags = { ...cached.flags };
    // ENG-7682 follow-up: hydrate the per-agent notify-dispatch map from the
    // SAME snapshot atomically, so a restart restores each agent's last-known
    // per-agent value rather than dropping it to the host-wide one until the
    // next new-schema heartbeat.
    this.notifyDispatchByAgent = { ...(cached.notify_dispatch_by_agent ?? {}) };
    this.lastSchemaVersion = cached.schema_version || '';
    const ageSeconds = flagsCacheAgeSeconds(cached, this.cachePath, now);
    const age = ageSeconds === null ? 'unknown' : `${Math.round(ageSeconds)}s`;
    const schema = cached.schema_version || 'unknown';
    this.log(
      `[flags] loaded last-known-good cache (age: ${age}, schema: ${schema}); using cached values until first heartbeat`,
    );
  }

  /**
   * Merge a heartbeat flag map: log per-flag transitions and env-masking warns,
   * replace the in-memory map (full map; unknown keys kept), and persist it.
   * A missing/non-object map is a no-op so an older API that omits the field
   * doesn't blow away a good cache.
   */
  applyHeartbeat(map: Record<string, FlagValue> | undefined, schemaVersion?: string): void {
    if (!map || typeof map !== 'object') return;
    const previous = this.heartbeatFlags;

    for (const definition of listFlagDefinitions()) {
      const before = normalizeFlagValue(definition, previous[definition.key]);
      const after = normalizeFlagValue(definition, map[definition.key]);
      if (after !== undefined && after !== before) {
        this.log(
          `[flags] ${definition.key}: ${before ?? '(default)'} -> ${after} (source: heartbeat)`,
        );
      }
      if (definition.envVar) {
        const envValue = coerceEnvValue(definition, this.env[definition.envVar]);
        const masking = envValue !== undefined && after !== undefined && envValue !== after;
        if (masking) {
          // Log only on transition: when masking newly begins, or when the
          // heartbeat value being masked changes. A stable override would
          // otherwise spam this WARN every heartbeat (ENG-6478).
          if (this.lastMaskWarn.get(definition.key) !== after) {
            this.log(
              `[flags] WARN env override ${definition.envVar}=${envValue} is masking heartbeat value ${after} for '${definition.key}' (env wins until unset)`,
            );
            this.lastMaskWarn.set(definition.key, after);
          }
        } else {
          // Masking stopped (override cleared, or heartbeat now agrees) — re-arm
          // so a future re-mask logs once more.
          this.lastMaskWarn.delete(definition.key);
        }
      }
    }

    this.heartbeatFlags = { ...map };
    this.lastSchemaVersion = schemaVersion ?? '';
    this.persistCache();
  }

  /**
   * Persist the host-wide flag map AND the per-agent notify-dispatch map to the
   * last-known-good cache in a single atomic write, so a restart hydrates both
   * from the same snapshot (ENG-7682 follow-up). Called by every mutator that
   * changes either map. A write failure is logged and swallowed: the in-memory
   * state stays authoritative and the next successful heartbeat re-persists.
   */
  private persistCache(): void {
    try {
      writeFlagsCache(this.cachePath, {
        schema_version: this.lastSchemaVersion,
        updated_at: new Date().toISOString(),
        flags: this.heartbeatFlags,
        notify_dispatch_by_agent: this.notifyDispatchByAgent,
      });
    } catch (err) {
      this.log(`[flags] cache write failed: ${(err as Error).message}`);
    }
  }

  /** Resolve one flag, or `undefined` if the key isn't registered. */
  resolve(key: string): ResolvedFlag | undefined {
    const definition = getFlagDefinition(key);
    if (!definition) return undefined;
    return resolveFlagFromLayers(definition, this.heartbeatFlags, this.env);
  }

  resolveAll(): ResolvedFlag[] {
    return resolveAllFlags(this.heartbeatFlags, this.env);
  }

  /**
   * Typed boolean accessor. Falls back to the registry default for the key
   * (and to `false` for an unknown/non-boolean key) so a caller can read a
   * gate without null-checking.
   */
  getBoolean(key: string): boolean {
    const definition = getFlagDefinition(key);
    const resolved = this.resolve(key);
    if (resolved && typeof resolved.value === 'boolean') return resolved.value;
    return definition?.flagType === 'boolean' ? definition.defaultValue : false;
  }

  /**
   * ENG-7891 / ADR-0049 slice 3: resolve a BOOLEAN flag FOR A SPECIFIC AGENT.
   * Precedence mirrors {@link getStringForAgent}:
   *
   *   env override  ->  per-agent heartbeat value  ->  host-wide value (getBoolean)
   *
   * The env override (if the flag declares one) stays highest precedence and
   * host-wide; otherwise a per-agent heartbeat override beats the host-wide
   * value, and an agent with no per-agent entry inherits the host-wide value.
   * A non-boolean/unknown key delegates to getBoolean. This lets a one-way-door
   * gate be armed one agent at a time (id-keyed-layout-migration) without an
   * opt-in fanning out to every agent on the host.
   */
  getBooleanForAgent(key: string, agentId: string | undefined): boolean {
    const definition = getFlagDefinition(key);
    if (!definition || definition.flagType !== 'boolean') return this.getBoolean(key);
    const envValue = definition.envVar
      ? coerceEnvValue(definition, this.env[definition.envVar])
      : undefined;
    if (typeof envValue === 'boolean') return envValue;
    if (agentId) {
      const perAgent = this.booleanFlagsByAgent[agentId]?.[key];
      if (typeof perAgent === 'boolean') return perAgent;
    }
    return this.getBoolean(key);
  }

  /**
   * Typed string accessor for enum flags. Falls back to the registry default
   * (and to `''` for an unknown/non-enum key).
   */
  getString(key: string): string {
    const definition = getFlagDefinition(key);
    const resolved = this.resolve(key);
    if (resolved && typeof resolved.value === 'string') return resolved.value;
    return definition?.flagType === 'enum' ? definition.defaultValue : '';
  }

  /**
   * ENG-7682 follow-up: merge the heartbeat's per-agent notify-dispatch map.
   * A missing/non-object map REPLACES with an empty map so a clear (agent
   * override removed → API stops sending the entry) takes effect within one
   * poll rather than pinning a stale opt-in. Values that aren't valid
   * notify-dispatch members are dropped (registry skew / malformed payload).
   */
  applyNotifyDispatchByAgent(map: Record<string, FlagValue> | undefined): void {
    const definition = getFlagDefinition('notify-dispatch');
    if (!map || typeof map !== 'object' || !definition) {
      this.notifyDispatchByAgent = {};
      // Persist the cleared map so a restart doesn't rehydrate a stale opt-in
      // from a previous snapshot (ENG-7682 follow-up).
      this.persistCache();
      return;
    }
    const next: Record<string, FlagValue> = {};
    for (const [agentId, raw] of Object.entries(map)) {
      const value = normalizeFlagValue(definition, raw);
      if (value !== undefined) next[agentId] = value;
    }
    this.notifyDispatchByAgent = next;
    // Persist alongside the host-wide map as last-known-good so a manager
    // restart restores each agent's per-agent value (ENG-7682 follow-up).
    this.persistCache();
  }

  /**
   * ENG-7891 / ADR-0049 slice 3: merge the heartbeat's per-agent BOOLEAN
   * override map (`agent_id -> flag_key -> bool`), read by getBooleanForAgent.
   * A missing/non-object map REPLACES with an empty map so a cleared per-agent
   * override takes effect within one poll rather than pinning a stale opt-in.
   * Only registered boolean keys with an actual boolean value are kept (registry
   * skew / malformed payloads are dropped). In-memory only - deliberately not
   * persisted (unlike notify-dispatch), so a restart falls back to the host-wide
   * value until the next heartbeat rather than rehydrating a stale opt-in for a
   * one-way-door migration.
   */
  applyBooleanFlagsByAgent(map: Record<string, Record<string, FlagValue>> | undefined): void {
    if (!map || typeof map !== 'object') {
      this.booleanFlagsByAgent = {};
      return;
    }
    const next: Record<string, Record<string, boolean>> = {};
    for (const [agentId, perAgent] of Object.entries(map)) {
      if (!perAgent || typeof perAgent !== 'object') continue;
      for (const [key, raw] of Object.entries(perAgent)) {
        const definition = getFlagDefinition(key);
        if (!definition || definition.flagType !== 'boolean') continue;
        if (typeof raw !== 'boolean') continue;
        (next[agentId] ??= {})[key] = raw;
      }
    }
    this.booleanFlagsByAgent = next;
  }

  /**
   * ENG-7682 follow-up: resolve an enum flag FOR A SPECIFIC AGENT. Precedence:
   *
   *   env override  ->  per-agent heartbeat value  ->  host-wide value (getString)
   *
   * The env override still wins (AGT_NOTIFY_DISPATCH stays the per-process
   * operator escape hatch); otherwise a per-agent heartbeat value beats the
   * host-wide one, and an agent with no per-agent entry inherits the host-wide
   * value unchanged. Only notify-dispatch is threaded per-agent today; other
   * enum flags stay host-wide, so a non-notify key just delegates to getString.
   */
  getStringForAgent(key: string, agentId: string | undefined): string {
    const definition = getFlagDefinition(key);
    if (!definition || definition.flagType !== 'enum') return this.getString(key);
    // Env override is highest precedence and is host-wide (per-process); if it
    // is set it masks any per-agent heartbeat value, same as the host-wide path.
    const envValue = definition.envVar
      ? coerceEnvValue(definition, this.env[definition.envVar])
      : undefined;
    if (envValue !== undefined && typeof envValue === 'string') return envValue;
    // Only notify-dispatch carries a per-agent heartbeat map today; any other
    // enum flag resolves host-wide (falls through to getString below).
    if (agentId && key === 'notify-dispatch') {
      const perAgent = this.notifyDispatchByAgent[agentId];
      if (typeof perAgent === 'string') {
        const normalized = normalizeFlagValue(definition, perAgent);
        if (typeof normalized === 'string') return normalized;
      }
    }
    return this.getString(key);
  }
}
